import { styled } from '@linaria/react'
import React, { FC, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import SvgPlus from '../svgr/Plus'
import { TagList } from '../ui/TagList'
import { Boxes } from '../components/Boxes'
import { colors } from '../styles/colors'
import { Link } from 'react-router'
import { createMoneyPoolPagePath } from './CreateMoneyPoolPage'
import { BoxStatus } from '../api/boxApi'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { onboardingPagePath } from './OnboardingPage'
import { useAppContext } from '../contexts/App.context'

const allTags = [
  {
    id: 'all',
    label: <FormattedMessage id="all" defaultMessage="Все" />,
  } as const,

  {
    id: 'my',
    label: <FormattedMessage id="my" defaultMessage="Мои" />,
    requiresAuth: true,
  } as const,

  {
    id: 'my-votes',
    label: <FormattedMessage id="my-votes" defaultMessage="Мои голоса" />,
    requiresAuth: true,
  } as const,
  {
    id: 'finished',
    label: <FormattedMessage id="finished" defaultMessage="Законченные" />,
  } as const,
]

export type TagId = (typeof allTags)[number]['id']

export const IndexPage: FC = () => {
  const [activeTag, setActiveTag] = useState<TagId>('all')
  const navigate = useNavigate()
  const { isCompleted: isOnboardingCompleted } = useOnboardingContext()
  const { authenticated } = useAppContext()

  // Filter tags based on authentication status
  const tags = allTags.filter(tag => !tag.requiresAuth || authenticated)

  // Check if onboarding should be shown when user lands on IndexPage
  useEffect(() => {
    if (isOnboardingCompleted === false) {
      navigate(onboardingPagePath)
    }
  }, [isOnboardingCompleted, navigate])

  // Reset active tag if user loses authentication and is on a protected tag
  useEffect(() => {
    const currentTag = allTags.find(tag => tag.id === activeTag)
    if (currentTag?.requiresAuth && !authenticated) {
      setActiveTag('all')
    }
  }, [activeTag, authenticated])

  const handleTagClick = (tagId: string) => {
    const validTag = tags.find(tag => tag.id === tagId)
    if (validTag) {
      setActiveTag(validTag.id)
    } else {
      throw new Error(`Invalid tag id: ${tagId}`)
    }
  }

  const getBoxesStatuses = (): BoxStatus => {
    switch (activeTag) {
      case 'all':
        return 'active'
      case 'my':
        return 'active'
      case 'finished':
        return 'finished'
      case 'my-votes':
        return 'all'
      default:
        return 'all'
    }
  }

  return (
    <IndexPageContainer>
      <Container>
        <TagList
          tags={tags}
          activeTagId={activeTag}
          onTagClick={handleTagClick}
        />

        <Boxes
          titleElement={null}
          tabsElement={null}
          status={getBoxesStatuses()}
          votedByMe={activeTag === 'my-votes'}
          createdByMe={activeTag === 'my'}
          displayFilterFn={box => box.feedDisplay && box.passedModeration}
        />
      </Container>

      <Link to={createMoneyPoolPagePath}>
        <CreateMoneyPoolButton>
          <SvgPlus />
        </CreateMoneyPoolButton>
      </Link>
    </IndexPageContainer>
  )
}

export const indexPagePath = '/'

const IndexPageContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-gap: 16px;
  align-content: flex-start;
  width: 100%;
`

const Container = styled.div`
  display: grid;
  gap: 12px;
`

const CreateMoneyPoolButton = styled.button`
  position: fixed;
  --padding-size: 8px;
  bottom: calc(var(--bottom-navigation-height) + var(--padding-size));
  right: var(--padding-size);
  background: ${colors.white};
  border-radius: 8px;
  padding: 23px;
  border-radius: 100%;
  border: 2px solid ${colors.secondary};
  cursor: pointer;
  z-index: 1;
`
